/**
 * Keeps an active session alive against a deliberately short token.
 *
 * ── Why the token is short ────────────────────────────────────────────
 * A JWT cannot be revoked. Once issued it is valid until it expires, so
 * its lifetime IS the window a leaked one stays useful. The industry
 * norm for access tokens is around an hour (Okta, Cognito, Google and
 * Entra all sit there) — but that norm assumes something renews the
 * session underneath, or people would be thrown out mid-task every hour.
 *
 * This is that something. It renews while the token is in its final
 * stretch, so:
 *
 *   * someone still working carries on indefinitely, never seeing it;
 *   * a laptop left on a bar counter holds a token that dies by itself
 *     within the hour, whatever happens to the browser.
 *
 * ── Why it checks rather than schedules ───────────────────────────────
 * A `setTimeout` for "renew in 50 minutes" does not survive a closed
 * laptop: the machine sleeps, the timer does not fire on time, and the
 * token is already dead when it does. Polling the token's own `exp`
 * against the wall clock is immune to that — waking up simply means the
 * next check finds it due.
 *
 * ── Why it does not fight the idle timeout ────────────────────────────
 * Renewal is not activity. It runs on a timer regardless, so it can
 * never keep an abandoned session alive: `useIdleTimeout` measures real
 * input and will sign an idle person out well before this matters.
 */

import { useEffect } from 'react';

import { renewRequest } from '../api/auth';
import { AUTH_TOKEN_KEY } from '../api/client';

/** How often to look at the clock. Cheap: it is a string parse. */
const CHECK_INTERVAL_MS = 60_000;

/**
 * Renew once the token is within this much of expiring.
 *
 * Fifteen minutes against a sixty-second check gives roughly fifteen
 * attempts before the token actually dies. That margin is not paranoia:
 * reps work in bars and back-offices on poor connections, and a single
 * failed renewal must never be the thing that signs someone out
 * mid-visit.
 */
const RENEW_WITHIN_MS = 15 * 60_000;

/** Milliseconds until the JWT expires, or null if it cannot be read. */
function msUntilExpiry(token: string): number | null {
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const { exp } = JSON.parse(atob(payload)) as { exp?: number };
    if (typeof exp !== 'number') return null;
    return exp * 1000 - Date.now();
  } catch {
    return null;
  }
}

export function useTokenRenewal(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;

    // Guards against two overlapping renewals — a slow response plus a
    // second tab waking at the same moment would otherwise both fire.
    let inFlight = false;

    const check = async () => {
      if (inFlight) return;
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      if (!token) return;

      const remaining = msUntilExpiry(token);
      // Unreadable token: leave it alone. The API client's 401 handling
      // is the right place for a broken credential, not this.
      if (remaining === null) return;
      // Already dead — renewing would 401. Let the next request surface
      // it and sign them out through the normal path.
      if (remaining <= 0) return;
      if (remaining > RENEW_WITHIN_MS) return;

      inFlight = true;
      try {
        const fresh = await renewRequest();
        localStorage.setItem(AUTH_TOKEN_KEY, fresh.access_token);
      } catch {
        // Deliberately silent. A failed renewal is not a failed session:
        // there is still time left on the current token, and the next
        // tick will try again. If they all fail, the token expires and
        // the normal 401 path signs them out — which is the correct
        // outcome, reached without ever showing an error for something
        // the person did not do.
      } finally {
        inFlight = false;
      }
    };

    void check();
    const timer = window.setInterval(() => void check(), CHECK_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [enabled]);
}
